// Full-screen session lock. Deliberately NOT the shared Modal: it must sit
// above every open modal, ignore the Escape stack, hide all PHI behind an
// opaque surface, and offer no dismissal other than unlock or sign-out.

import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Lock, WifiOff } from 'lucide-react';
import { connectivityMonitor } from '@/services/offline/connectivityMonitor';
import { useOfflineSessionStore } from '@/services/offline/offlineSessionStore';
import { unlockSession, confirmLogout } from '@/services/offline/offlineSession';
import { useConnectivity } from '@/hooks/useConnectivity';
import { authService } from '@/services/auth';
import { performLogout } from '@/utils/logoutCleanup';

export default function OfflineLockScreen() {
  const { logout } = useAuth0();
  const lockReason = useOfflineSessionStore((s) => s.lockReason);
  const { isOnline } = useConnectivity();
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);
  const expired = lockReason === 'trust-window-expired';

  // Focus trap: keep Tab cycling inside the overlay.
  useEffect(() => {
    const node = containerRef.current;
    node?.querySelector('button')?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node?.querySelectorAll('button:not([disabled])');
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // Capture phase so the app's own Escape handling never sees the event.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // A hardened (trust-window-expired) lock retries automatically once the
  // backend is reachable again.
  useEffect(() => {
    if (!expired) return;
    const unsubscribe = connectivityMonitor.subscribe((online) => {
      if (online) unlockSession();
    });
    connectivityMonitor.probeNow();
    return unsubscribe;
  }, [expired]);

  const handleContinue = async () => {
    setBusy(true);
    try {
      await unlockSession();
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    try {
      if (!(await confirmLogout())) return;
      authService.clearToken();
      await performLogout(logout);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Session locked"
      className="fixed inset-0 z-[1000] bg-gray-100 flex items-center justify-center px-4"
    >
      <div className="max-w-sm w-full bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          {expired ? <WifiOff className="w-6 h-6 text-gray-500" /> : <Lock className="w-6 h-6 text-gray-500" />}
        </div>
        <h1 className="text-lg font-semibold text-gray-900">Session locked</h1>
        {expired ? (
          <p className="mt-2 text-sm text-gray-600">
            Offline access ends 24 hours after your last connection. This screen
            unlocks automatically once a connection is available.
          </p>
        ) : (
          <p className="mt-2 text-sm text-gray-600">
            Locked after inactivity.{' '}
            {isOnline
              ? 'Continue to verify your session and pick up where you left off.'
              : 'You are offline; your work is saved on this device and will sync when connection returns.'}
          </p>
        )}
        {!expired && (
          <button
            onClick={handleContinue}
            disabled={busy}
            className="mt-6 w-full px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Checking…' : 'Continue'}
          </button>
        )}
        <button
          onClick={handleSignOut}
          disabled={busy}
          className="mt-3 w-full px-4 py-2 text-sm font-medium rounded-md text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
